import {
    Container,
    Row,
    Col,
    Form,
    Button,
    FormControl,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import InputLogoField from '@/components/common/InputLogoField';
import { useAuth } from '@/shared/contexts/AuthContext';
import useUserApi from '@/shared/services/userApi';
import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';
import logger from '@/shared/utils/logger';
import sustainabilityHubLogo from '@/assets/img/logos/png/blueBackground.png';
import userIcon from '@/assets/img/icons/svg/userIcon.svg';
import peopleIcon from '@/assets/img/icons/svg/peopleIcon.svg';
import SEO from '@/components/common/SEO';

import './ProfilePage.css';

const EditProfile = () => {
    const { fetchProfile, updateProfile } = useUserApi();
    const { user, loading } = useAuth();
    const [error, setError] = useState({});
    const [profile, setProfile] = useState(null);
    const [validated, setValidated] = useState(false);
    const [changed, setChanged] = useState(false);

    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);

    const navigate = useNavigate();

    const linkRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

    const [formData, setFormData] = useState({
        firstName: 'No Name Given',
        lastName: 'No Name Given',
        bio: '',
        userType: '',
        organization: '',
        interests: [],
        locations: [],
        externalLinks: [],
        profileVisibility: {
            bio: false,
            interests: false,
            organization: false,
            externalLinks: false,
            userType: false,
            postsAndComments: false,
        },
    });

    const [tempInputs, setTempInputs] = useState({
        interests: '',
        locations: '',
        externalLinks: '',
    });

    const { uploadProfilePicture } = useUserApi();

    const userTypes = [
        'student',
        'professor',
        'researcher',
        'industry',
        'government',
    ];

    useEffect(() => {
        let retryTimer = null;
        let cancelled = false;

        const fetchUser = async (retryCount = 0) => {
            try {
                const profileData = await fetchProfile();
                if (cancelled) return;

                setProfile(profileData);

                setFormData((prev) => ({
                    ...prev,
                    firstName:
                        profileData?.name?.first || 'No First Name Given',
                    lastName: profileData?.name?.last || 'No Last Name Given',
                    bio: profileData.bio || '',
                    userType: profileData.userType || '',
                    organization: profileData.organization || '',
                    interests: profileData.interests || [],
                    locations: profileData.locations || [],
                    externalLinks: profileData.externalLinks || [],
                    profileVisibility: {
                        bio: profileData.profileVisibility?.bio ?? false,
                        interests:
                            profileData.profileVisibility?.interests ?? false,
                        organization:
                            profileData.profileVisibility?.organization ??
                            false,
                        externalLinks:
                            profileData.profileVisibility?.externalLinks ??
                            false,
                        userType:
                            profileData.profileVisibility?.userType ?? false,
                        postsAndComments:
                            profileData.profileVisibility?.postsAndComments ??
                            false,
                    },
                }));
            } catch (err) {
                if (cancelled) return;
                if (err.response?.status === 404 && retryCount < 3) {
                    retryTimer = setTimeout(
                        () => {
                            fetchUser(retryCount + 1);
                        },
                        (retryCount + 1) * 2000
                    );
                    return;
                }

                setError((prevErrors) => ({
                    ...prevErrors,
                    load:
                        err.response?.data?.message ||
                        err.message ||
                        'Failed to load profile.',
                }));
            }
        };

        if (user && !loading) {
            fetchUser();
        }

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [fetchProfile, loading, user]);

    const handleKeyPress = (e, type) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddItem(type, tempInputs[type]);
        }
    };

    const handleAddItem = (type, value) => {
        if (value.trim()) {
            if (type === 'externalLinks' && !linkRegex.test(value.trim())) {
                setError((prevErrors) => ({
                    ...prevErrors,
                    externalLinks:
                        'Invalid link. Must start with http:// or https://',
                }));
                setValidated(true);
                setTempInputs({ ...tempInputs, externalLinks: '' });
                return;
            }

            if (!value) return;

            if (type !== 'externalLinks') {
                const fieldRegex = /^[a-zA-Z-' ]{2,20}$/;

                if (
                    formData[type].includes(capitalizeFirst(value)) ||
                    !fieldRegex.test(value.trim())
                ) {
                    return;
                }
            }

            // External links are stored verbatim. interests/locations are
            // normalized to "Capitalized" form for consistent display and
            // de-duplication. Capitalizing a URL would corrupt it
            // (capitalizeFirst also lowercases the remainder, mangling the
            // host, path, and query), so links must skip normalization.
            const normalizedValue =
                type === 'externalLinks'
                    ? value.trim()
                    : capitalizeFirst(value.trim());
            const updatedFormData = {
                ...formData,
                [type]: [...formData[type], normalizedValue],
            };
            setFormData(updatedFormData);
            setTempInputs({ ...tempInputs, [type]: '' });
            setError(validate(updatedFormData));
            setChanged(true);
        }
    };

    const handleRemoveItem = (type, index) => {
        const updatedFormData = {
            ...formData,
            [type]: formData[type].filter((_, i) => i !== index),
        };
        setFormData(updatedFormData);
        setError(validate(updatedFormData));
        setChanged(true);
    };

    const handleEditProfile = async (event) => {
        event.preventDefault();

        // Auto-add any pending input before saving
        let updatedFormData = { ...formData };
        if (tempInputs.interests.trim()) {
            const fieldRegex = /^[a-zA-Z-' ]{2,20}$/;
            if (
                fieldRegex.test(tempInputs.interests.trim()) &&
                !formData.interests.includes(
                    capitalizeFirst(tempInputs.interests)
                )
            ) {
                updatedFormData.interests = [
                    ...formData.interests,
                    capitalizeFirst(tempInputs.interests.trim()),
                ];
            }
        }
        if (tempInputs.locations.trim()) {
            const fieldRegex = /^[a-zA-Z-' ]{2,20}$/;
            if (
                fieldRegex.test(tempInputs.locations.trim()) &&
                !formData.locations.includes(
                    capitalizeFirst(tempInputs.locations)
                )
            ) {
                updatedFormData.locations = [
                    ...formData.locations,
                    capitalizeFirst(tempInputs.locations.trim()),
                ];
            }
        }
        if (
            tempInputs.externalLinks.trim() &&
            linkRegex.test(tempInputs.externalLinks.trim())
        ) {
            updatedFormData.externalLinks = [
                ...formData.externalLinks,
                tempInputs.externalLinks.trim(),
            ];
        }
        setFormData(updatedFormData);
        setTempInputs({ interests: '', locations: '', externalLinks: '' });

        const newErrors = validate(updatedFormData);
        setError(newErrors);

        if (newErrors.firstName || newErrors.lastName) {
            setValidated(true);
            setErrorMessage(
                'Please add both a first and last name and try again.'
            );
            setSuccessMessage('');
            return;
        }

        if (newErrors.userType) {
            setValidated(true);
            setErrorMessage('Please select a user type and try again.');
            setSuccessMessage('');
            return;
        }

        if (!user) {
            navigate('/login');
            return;
        }

        setValidated(true);

        try {
            const profileData = {
                name: {
                    first: updatedFormData.firstName,
                    last: updatedFormData.lastName,
                },
                bio: updatedFormData.bio,
                userType: updatedFormData.userType,
                organization: updatedFormData.organization,
                interests: updatedFormData.interests,
                locations: updatedFormData.locations,
                externalLinks: updatedFormData.externalLinks,
                profileVisibility: updatedFormData.profileVisibility,
            };

            await updateProfile(profileData);

            setSuccessMessage('Your profile has been updated successfully!');
            setErrorMessage('');
            setChanged(false);

            if (profilePicture) {
                try {
                    const formData = new FormData();
                    formData.append('profilePicture', profilePicture);
                    await uploadProfilePicture(formData);
                } catch (uploadError) {
                    logger.error('Profile picture upload failed:', uploadError);
                }
            }

            setTimeout(() => navigate('/profile'), 1000);
        } catch (error) {
            setErrorMessage('Profile edit failed. Please try again.');
            setSuccessMessage('');
            setChanged(true);
            logger.error('Profile edit failed. Please try again. ', error);
        }
    };

    const handleCancel = () => {
        navigate('/profile');
    };

    const validate = (values) => {
        const error = {};
        const fieldRegex = /^[a-zA-Z-' ]{2,20}$/;

        if (!values.firstName.trim()) {
            error.firstName = 'First name is required';
        } else if (!fieldRegex.test(values.firstName.trim())) {
            error.firstName =
                'First name must be 2-20 characters and contain only letters, spaces, hyphens, and apostrophes';
        }

        if (!values.lastName.trim()) {
            error.lastName = 'Last name is required';
        } else if (!fieldRegex.test(values.lastName.trim())) {
            error.lastName =
                'Last name must be 2-20 characters and contain only letters, spaces, hyphens, and apostrophes';
        }

        if (values.bio && values.bio.length > 500) {
            error.bio = 'Bio must be less than 500 characters';
        }

        if (!values.userType) {
            error.userType = 'Please select a user type';
        }

        if (values.organization && values.organization.trim().length < 2) {
            error.organization =
                'Organization name must be at least 2 characters';
        }

        if (values.externalLinks && values.externalLinks.length > 0) {
            const invalid = values.externalLinks.filter(
                (u) => !linkRegex.test(u)
            );
            if (invalid.length) {
                error.externalLinks =
                    'Some links are invalid (must start with http:// or https://)';
            }
        }

        return error;
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validationError = validateImageFile(file);
        if (validationError) {
            setError({ ...error, profilePicture: validationError });
            return;
        }

        // Revoke previous object URL to prevent memory leak
        if (profilePicturePreview) {
            URL.revokeObjectURL(profilePicturePreview);
        }
        setError({ ...error, profilePicture: null });
        setProfilePicture(file);
        setProfilePicturePreview(URL.createObjectURL(file));
        setChanged(true);
    };

    const getProfilePictureUrl = () => {
        return (
            profilePicturePreview ||
            profile?.profilePicture ||
            sustainabilityHubLogo
        );
    };

    const capitalizeFirst = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    return (
        <div id="create-profile" className="bg-primary">
            <SEO title="Edit Profile" />
            <Container className="m-auto py-5">
                <Form
                    noValidate
                    className="create-profile-form d-flex flex-column align-items-center bg-white gap-3 mx-auto w-100 py-5 px-5"
                    onSubmit={handleEditProfile}
                >
                    <Row className="mb-1">
                        <h1 className="text-primary text-center">
                            Edit Your Profile
                        </h1>
                    </Row>

                    {error.load && (
                        <div
                            className="alert alert-danger w-100 text-center focus-ring"
                            role="alert"
                        >
                            {error.load}
                        </div>
                    )}

                    <Row className="mb-3 w-100">
                        <Col>
                            <div className="text-center">
                                <p className="text-muted mb-2">
                                    <strong>Profile Picture</strong> (Optional)
                                </p>

                                {/* profile picture preview */}
                                <div className="mb-3">
                                    <img
                                        src={getProfilePictureUrl()}
                                        alt="Profile preview"
                                        className="profile-picture-preview"
                                    />
                                </div>

                                {/* file input */}
                                <div className="profile-picture-input-wrapper">
                                    <Form.Control
                                        type="file"
                                        accept={ACCEPTED_FILE_EXTENSIONS}
                                        onChange={handleFileChange}
                                        disabled={loading}
                                        className="profile-picture-input"
                                        aria-label="Upload profile picture"
                                        aria-describedby="profile-picture-help"
                                        aria-invalid={!!error.profilePicture}
                                    />
                                    {error.profilePicture && (
                                        <div className="invalid-feedback d-block">
                                            {error.profilePicture}
                                        </div>
                                    )}
                                    <Form.Text
                                        id="profile-picture-help"
                                        className="text-muted d-block mt-1 text-center"
                                    >
                                        JPEG, PNG, or HEIC (max 5MB)
                                    </Form.Text>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Form.Group className="w-100 mb-3">
                        <InputLogoField
                            icon={userIcon}
                            controlId="floatingUsername"
                            label="Username"
                        >
                            <Form.Control
                                type="text"
                                value={profile?.username || ''}
                                disabled
                            />
                        </InputLogoField>
                        <Form.Text className="text-muted ps-3">
                            Username cannot be changed.
                        </Form.Text>
                    </Form.Group>

                    <Row className="flex-xl-nowrap">
                        <Form.Group as={Col} xl="6" className="mb-3">
                            <InputLogoField
                                icon={userIcon}
                                controlId="floatingFirstName"
                                label="First Name *"
                                hasValidation
                            >
                                <Form.Control
                                    type="text"
                                    placeholder="No Name Given"
                                    value={formData?.firstName}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const updatedForm = {
                                            ...formData,
                                            firstName: val,
                                        };
                                        setFormData(updatedForm);
                                        setError(validate(updatedForm));
                                        setChanged(true);
                                    }}
                                    isValid={formData.firstName.trim() !== ''}
                                    isInvalid={validated && !!error.firstName}
                                    disabled={loading}
                                />
                            </InputLogoField>
                            {validated && error.firstName && (
                                <div className="invalid-feedback d-block ps-3">
                                    {error.firstName}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group as={Col} xl="6" className="mb-3">
                            <InputLogoField
                                icon={userIcon}
                                controlId="floatingLastName"
                                label="Last Name *"
                                hasValidation
                            >
                                <Form.Control
                                    type="text"
                                    placeholder={
                                        profile?.name?.last ||
                                        'Enter your last name'
                                    }
                                    value={formData?.lastName}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const updatedForm = {
                                            ...formData,
                                            lastName: val,
                                        };
                                        setFormData(updatedForm);
                                        setError(validate(updatedForm));
                                        setChanged(true);
                                    }}
                                    isValid={formData.lastName.trim() !== ''}
                                    isInvalid={validated && !!error.lastName}
                                    disabled={loading}
                                />
                            </InputLogoField>
                            {validated && error.lastName && (
                                <div className="invalid-feedback d-block ps-3">
                                    {error.lastName}
                                </div>
                            )}
                        </Form.Group>
                    </Row>
                    <Form.Group className="w-100 mb-3">
                        <InputLogoField
                            icon={peopleIcon}
                            controlId="floatingUserType"
                            label="User Type *"
                            hasValidation
                        >
                            <Form.Select
                                value={formData.userType}
                                onChange={(e) => {
                                    const updatedForm = {
                                        ...formData,
                                        userType: e.target.value,
                                    };
                                    setChanged(true);
                                    setFormData(updatedForm);
                                    setError(validate(updatedForm));
                                }}
                                isValid={formData.userType != ''}
                                isInvalid={validated && !!error.userType}
                            >
                                <option value="">Select user type</option>
                                {userTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type.charAt(0).toUpperCase() +
                                            type.slice(1)}
                                    </option>
                                ))}
                            </Form.Select>
                        </InputLogoField>
                    </Form.Group>

                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            Bio (Optional)
                        </Form.Label>
                        <FormControl
                            as="textarea"
                            placeholder={
                                profile?.bio || 'Tell us about yourself!'
                            }
                            value={formData.bio}
                            onChange={(e) => {
                                const updatedForm = {
                                    ...formData,
                                    bio: e.target.value,
                                };
                                setChanged(true);
                                setFormData(updatedForm);
                                setError(validate(updatedForm));
                            }}
                            isInvalid={!!error.bio}
                            isValid={!error.bio && formData.bio.trim() !== ''}
                            disabled={loading}
                        />
                        {validated && error.bio && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {error.bio}
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            Organization (Optional)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter Organization"
                            value={formData?.organization}
                            maxLength={50}
                            onChange={(e) => {
                                const updatedForm = {
                                    ...formData,
                                    organization: e.target.value,
                                };
                                setFormData(updatedForm);
                                setChanged(true);
                                setError(validate(updatedForm));
                            }}
                            isInvalid={!!error.organization}
                            isValid={
                                !error.organization &&
                                formData.organization.trim() !== ''
                            }
                            disabled={loading}
                        />
                        {validated && error.organization && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {error.organization}
                            </div>
                        )}
                    </Form.Group>

                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            Interests (Optional, press Enter to add)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Add an interest..."
                            value={tempInputs.interests}
                            onChange={(e) => {
                                setTempInputs({
                                    ...tempInputs,
                                    interests: e.target.value,
                                });
                                setChanged(true);
                            }}
                            onKeyDown={(e) => {
                                handleKeyPress(e, 'interests');
                            }}
                            disabled={loading}
                        />
                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.interests.map((interest, index) => (
                                <span
                                    key={interest}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {interest}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem('interests', index)
                                        }
                                        aria-label={`Remove ${interest}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                    </Form.Group>

                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            Locations (Optional, press Enter to add)
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter a Colorado city or region..."
                            value={tempInputs.locations}
                            onChange={(e) => {
                                setTempInputs({
                                    ...tempInputs,
                                    locations: e.target.value,
                                });
                                setChanged(true);
                            }}
                            onKeyDown={(e) => {
                                handleKeyPress(e, 'locations');
                            }}
                            disabled={loading}
                        />
                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.locations.map((location, index) => (
                                <span
                                    key={location}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {location}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem('locations', index)
                                        }
                                        aria-label={`Remove ${location}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                    </Form.Group>

                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            External Links (Optional, press Enter to add)
                        </Form.Label>

                        <Form.Control
                            type="url"
                            placeholder="Add a link (must start with http:// or https://)..."
                            value={tempInputs.externalLinks}
                            onChange={(e) => {
                                setTempInputs({
                                    ...tempInputs,
                                    externalLinks: e.target.value,
                                });
                                setChanged(true);
                            }}
                            onKeyDown={(e) => {
                                handleKeyPress(e, 'externalLinks');
                            }}
                            disabled={loading}
                        />

                        <div className="mt-2 d-flex flex-wrap gap-2">
                            {formData.externalLinks.map((link, index) => (
                                <span
                                    key={link}
                                    className="badge bg-primary d-flex align-items-center gap-1"
                                >
                                    {link}
                                    <button
                                        type="button"
                                        className="btn-close btn-close-white"
                                        onClick={() =>
                                            handleRemoveItem(
                                                'externalLinks',
                                                index
                                            )
                                        }
                                        aria-label={`Remove ${link}`}
                                        disabled={loading}
                                    />
                                </span>
                            ))}
                        </div>
                        {validated && error.externalLinks && (
                            <div className="invalid-feedback d-block fw-semibold ps-3">
                                {error.externalLinks}
                            </div>
                        )}
                    </Form.Group>
                    <Form.Group className="w-100 mb-3">
                        <Form.Label className="fw-semibold">
                            Public Profile Visibility
                        </Form.Label>
                        <p className="text-muted small mb-2">
                            Control what others can see on your public profile.
                        </p>
                        <Form.Check
                            type="switch"
                            id="visibility-bio"
                            label="Show bio on public profile"
                            checked={formData.profileVisibility.bio}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        bio: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                        />
                        <Form.Check
                            type="switch"
                            id="visibility-interests"
                            label="Show interests on public profile"
                            checked={formData.profileVisibility.interests}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        interests: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                            className="mt-2"
                        />
                        <Form.Check
                            type="switch"
                            id="visibility-organization"
                            label="Show organization on public profile"
                            checked={formData.profileVisibility.organization}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        organization: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                            className="mt-2"
                        />
                        <Form.Check
                            type="switch"
                            id="visibility-externalLinks"
                            label="Show external links on public profile"
                            checked={formData.profileVisibility.externalLinks}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        externalLinks: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                            className="mt-2"
                        />
                        <Form.Check
                            type="switch"
                            id="visibility-userType"
                            label="Show role on public profile"
                            checked={formData.profileVisibility.userType}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        userType: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                            className="mt-2"
                        />
                        <Form.Check
                            type="switch"
                            id="visibility-postsAndComments"
                            label="Show posts and comments on public profile"
                            checked={
                                formData.profileVisibility.postsAndComments
                            }
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    profileVisibility: {
                                        ...formData.profileVisibility,
                                        postsAndComments: e.target.checked,
                                    },
                                });
                                setChanged(true);
                            }}
                            disabled={loading}
                            className="mt-2"
                        />
                    </Form.Group>

                    {successMessage && (
                        <div
                            className="alert alert-success w-100 text-center focus-ring"
                            role="alert"
                        >
                            {successMessage}
                        </div>
                    )}

                    {errorMessage && (
                        <div
                            className="alert alert-danger w-100 text-center focus-ring"
                            role="alert"
                        >
                            {errorMessage}
                        </div>
                    )}
                    <div className="w-100 mt-4 d-flex flex-wrap justify-content-center gap-2">
                        <Button
                            className="log-out-btn"
                            type="submit"
                            disabled={!changed || loading}
                        >
                            {loading ? 'Updating Profile...' : 'Save Changes'}
                        </Button>
                        <Button
                            className="log-out-btn"
                            type="button"
                            disabled={loading}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            className="log-out-btn"
                            type="button"
                            disabled={loading}
                            onClick={() => navigate('/dashboard')}
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                </Form>
            </Container>
        </div>
    );
};

export default EditProfile;
